// src/composables/useProductEdit.ts
import { ref, Ref, watch } from 'vue';
import api, { putFormData } from '@/services/api';
import { useRouter, useRoute } from 'vue-router';
import categoryService from '@/services/categoryService';
import { getImageUrl } from '@/utils/imageUtils';

// Interface za kategoriju
interface Category {
  id: string;
  name: string;
  parent?: {
    id: string;
    name: string;
  };
}

// Interface za sliku proizvoda
interface ProductImage {
  id?: string;
  path?: string;
  order: number;
  isMain: boolean;
  file?: File;
  preview?: string;
  rotation?: number;
  isExisting?: boolean; // Flag za razlikovanje postojećih i novih slika
}

// Interface za formu proizvoda
interface ProductFormData {
  id: string;
  name: string;
  description: string;
  price: string;
  categoryId: string;
  images: ProductImage[];
  imagesToDelete: string[];
  slug?: string;
  createdAt?: string;
  updatedAt?: string;
  expiresAt?: string;
  renewedAt?: string | null;
}

// Interface za odgovor s proizvoda
interface ProductResponse {
  status: string;
  message: string;
  data: {
    id: string;
    name: string;
    slug: string;
    description: string;
    price: string;
    stock: number;
    isActive: boolean;
    userId: string;
    pictures: {
      id: string;
      path: string;
      order: number;
      isMain: boolean;
    }[];
    mainPicture: string;
    category: Category;
    createdAt: string;
    updatedAt: string;
    expiresAt: string;
    renewedAt: string | null;
  };
}

export function useProductEdit() {
  const router = useRouter();
  const route = useRoute();
  const formData = ref<ProductFormData>({
    id: '',
    name: '',
    description: '',
    price: '',
    categoryId: '',
    images: [],
    imagesToDelete: [],
  });

  // Za praćenje stanja
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const successMessage = ref<string | null>(null);
  const categories = ref<Category[]>([]);
  const isDataLoaded = ref(false);
  const originalProduct = ref<any>(null);

  // Toast notification state
  const toast = ref({
    show: false,
    message: '',
    type: 'info' as 'success' | 'error' | 'info' | 'warning',
  });

  const showToast = (
    message: string,
    type: 'success' | 'error' | 'info' | 'warning' = 'info'
  ) => {
    toast.value = {
      show: true,
      message,
      type,
    };

    // Auto-hide toast after 3 seconds
    setTimeout(() => {
      toast.value.show = false;
    }, 3000);
  };

  // Učitavanje kategorija
  const loadCategories = async () => {
    try {
      categories.value = await categoryService.getCategories();

      if (categories.value.length === 0) {
        error.value =
          'Nije moguće učitati kategorije. Pokušajte ponovo kasnije.';
      }
    } catch (err) {
      console.error('Greška pri učitavanju kategorija:', err);
      error.value = 'Nije moguće učitati kategorije. Pokušajte ponovo kasnije.';
    }
  };

  // Učitavanje proizvoda
  const loadProduct = async (productId: string) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await api.get(`/api/products/id/${productId}`);

      if (response.data?.status === 'success') {
        const productData = response.data.data;
        originalProduct.value = { ...productData }; // Čuvamo originalnu verziju za poređenje

        formData.value = {
          id: productData.id,
          name: productData.name,
          description: productData.description,
          price: productData.price,
          categoryId: productData.category.id,
          images: [],
          imagesToDelete: [],
          slug: productData.slug,
          createdAt: productData.createdAt,
          updatedAt: productData.updatedAt,
          expiresAt: productData.expiresAt,
          renewedAt: productData.renewedAt,
        };

        // Konverzija slika u format za formu
        if (productData.pictures && productData.pictures.length > 0) {
          // Sortiraj slike po order polju
          const sortedPictures = [...productData.pictures].sort(
            (a, b) => a.order - b.order
          );

          formData.value.images = sortedPictures.map((pic) => {
            return {
              id: pic.id,
              path: pic.path,
              order: pic.order,
              isMain: pic.isMain,
              preview: getImageUrl(pic.path),
              rotation: 0,
              isExisting: true,
            };
          });
        }

        isDataLoaded.value = true;
      } else {
        throw new Error('Proizvod nije pronađen');
      }
    } catch (err) {
      console.error('Greška pri učitavanju proizvoda:', err);
      error.value = 'Nije moguće učitati proizvod. Pokušajte ponovo kasnije.';
      showToast('Nije moguće učitati proizvod', 'error');
    } finally {
      isLoading.value = false;
    }
  };

  // Dodavanje slike
  const addImage = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const newImage: ProductImage = {
        order: formData.value.images.length + 1,
        isMain: formData.value.images.length === 0,
        file,
        preview: e.target?.result as string,
        rotation: 0,
        isExisting: false,
      };
      formData.value.images.push(newImage);

      console.log(
        'Slike nakon dodavanja:',
        formData.value.images.map((img) => ({
          order: img.order,
          isMain: img.isMain,
          fileName: img.file?.name || 'Existing',
          id: img.id || 'New',
        }))
      );
    };
    reader.readAsDataURL(file);
  };

  // Brisanje slike
  const removeImage = (index: number) => {
    const imageToRemove = formData.value.images[index];

    // Ako je postojeća slika, dodaj je u listu za brisanje na serveru
    if (imageToRemove.isExisting && imageToRemove.id) {
      formData.value.imagesToDelete.push(imageToRemove.id);
    }

    // Brišemo sliku iz lokalnog niza
    formData.value.images.splice(index, 1);

    // Ažuriramo order za sve slike
    formData.value.images.forEach((img, idx) => {
      img.order = idx + 1;
      // Backend ne koristi isMain, ali ćemo ga ažurirati za UI
      img.isMain = idx === 0;
    });

    console.log(
      'Slike nakon brisanja:',
      formData.value.images.map((img) => ({
        order: img.order,
        isMain: img.isMain,
        fileName: img.file?.name || 'Existing',
        id: img.id || 'New',
      }))
    );

    console.log('Images to delete:', formData.value.imagesToDelete);
  };

  // Postavljanje glavne slike
  const setMainImage = (index: number) => {
    // Pronađemo sliku koju želimo postaviti kao glavnu
    const imageToSetAsMain = { ...formData.value.images[index] };

    if (!imageToSetAsMain) return;

    // Uklonimo tu sliku iz niza
    formData.value.images.splice(index, 1);

    // Postavimo je kao prvu u nizu
    formData.value.images.unshift(imageToSetAsMain);

    // Ažuriramo order za sve slike
    formData.value.images.forEach((img, idx) => {
      img.order = idx + 1;
      // Backend ne koristi isMain, ali ćemo ga ažurirati za UI
      img.isMain = idx === 0;
    });

    console.log(
      'Slike nakon postavljanja glavne:',
      formData.value.images.map((img) => ({
        order: img.order,
        isMain: img.isMain,
        fileName: img.file?.name || 'Existing',
        id: img.id || 'New',
      }))
    );
  };

  // Rotiranje slike
  const rotateImage = (index: number) => {
    const image = formData.value.images[index];
    const currentRotation = image.rotation || 0;
    const newRotation = (currentRotation + 90) % 360;

    image.rotation = newRotation;
  };

  // Funkcija koja rotira sliku i vraća novi File objekat
  const applyRotationToImage = (file: File, degrees: number): Promise<File> => {
    return new Promise((resolve, reject) => {
      if (degrees === 0) {
        resolve(file); // Nema rotacije, vrati original
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        if (!event.target?.result) {
          reject(new Error('Failed to read file'));
          return;
        }

        const img = new Image();
        img.onload = () => {
          // Kreiramo canvas za rotaciju
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          // Računamo novu širinu i visinu ako je rotacija 90 ili 270 stepeni
          let width = img.width;
          let height = img.height;

          if (degrees === 90 || degrees === 270) {
            // Zamena širine i visine
            width = img.height;
            height = img.width;
          }

          // Postavljamo dimenzije canvas-a
          canvas.width = width;
          canvas.height = height;

          // Transliramo i rotiramo kontekst
          ctx.translate(width / 2, height / 2);
          ctx.rotate((degrees * Math.PI) / 180);

          // Crtamo rotiranu sliku
          ctx.drawImage(img, -img.width / 2, -img.height / 2);

          // Konvertujemo canvas u blob
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob from canvas'));
              return;
            }

            // Kreiramo novi File objekat sa rotiranom slikom
            const rotatedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: file.lastModified,
            });

            resolve(rotatedFile);
          }, file.type);
        };

        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };

        // Postavimo src slike na učitani fajl
        img.src = event.target.result as string;
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      // Čitamo fajl kao Data URL
      reader.readAsDataURL(file);
    });
  };

  // Fetch image as File from URL (for existing image rotation)
  const fetchImageAsFile = async (
    url: string,
    fileName: string
  ): Promise<File> => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new File([blob], fileName, { type: blob.type });
    } catch (error) {
      console.error('Error fetching image as file:', error);
      throw error;
    }
  };

  interface ProductChanges {
    name?: string;
    description?: string;
    price?: string;
    categoryId?: string;
    imagesToDelete?: string[];
    imageOrder?: string;
    mainImageId?: string;
    images?: File[];
    rotatedImages?: {
      file: File;
      origId: string;
    }[];
  }

  // Definišimo interface za sliku iz API-ja
  interface PictureResponse {
    id: string;
    path: string;
    order: number;
    isMain: boolean;
  }

  // Ažuriranje proizvoda
  const updateProduct = async () => {
    isLoading.value = true;
    error.value = null;
    successMessage.value = null;

    try {
      // Validacija forme pre slanja
      const validation = await validateForm();
      if (!validation.isValid) {
        const firstError = Object.values(validation.errors)[0];
        error.value = firstError;
        showToast(firstError, 'error');
        isLoading.value = false;
        return;
      }

      // Provera da li ima promena
      let hasChanges = false;

      // Proveravamo osnovne podatke
      if (
        originalProduct.value.name !== formData.value.name ||
        originalProduct.value.description !== formData.value.description ||
        originalProduct.value.price !== formData.value.price ||
        originalProduct.value.category.id !== formData.value.categoryId ||
        formData.value.images.length !==
          originalProduct.value.pictures.length ||
        formData.value.imagesToDelete.length > 0 ||
        formData.value.images.some((img) => !img.isExisting) ||
        formData.value.images.some((img) => img.rotation && img.rotation > 0)
      ) {
        hasChanges = true;
      }

      // Ako nema promena, obaveštavamo korisnika i završavamo
      if (!hasChanges) {
        successMessage.value = 'Nema promena za ažuriranje!';
        showToast('Nema promena za ažuriranje!', 'info');
        isLoading.value = false;
        return;
      }

      // Identifikujemo da li je nova slika glavna slika
      const isNewImageMain =
        formData.value.images.length > 0 &&
        !formData.value.images[0].isExisting;

      // Identifikujemo da li je rotirana slika glavna slika
      const isRotatedImageMain =
        formData.value.images.length > 0 &&
        formData.value.images[0].isExisting &&
        formData.value.images[0].rotation &&
        formData.value.images[0].rotation > 0;

      // Pratimo specijalni slučaj kada je glavna slika nova ili rotirana
      const needsMainImageUpdate = isNewImageMain || isRotatedImageMain;

      // Kreiranje FormData objekta za slanje
      const submitData = new FormData();

      // KLJUČNA ISPRAVKA: UVEK šaljemo sve osnovne podatke, bez obzira na to da li su promenjeni
      submitData.append('name', formData.value.name);
      submitData.append('description', formData.value.description);
      submitData.append('price', formData.value.price);
      submitData.append('categoryId', formData.value.categoryId);

      // ----- PROCES DODAVANJA NOVIH SLIKA -----
      const newImages = formData.value.images.filter(
        (img) => !img.isExisting && img.file
      );

      // ----- PROCES ROTACIJE POSTOJEĆIH SLIKA -----
      const existingImagesToRotate = formData.value.images.filter(
        (img) => img.isExisting && img.rotation && img.rotation > 0 && img.path
      );

      // Čuvamo informacije o pozicijama slika pre rotacije
      const rotatedImagesInfo = existingImagesToRotate.map((img) => ({
        id: img.id,
        order: img.order,
        isMain: img.isMain,
      }));

      // Slike za brisanje
      if (formData.value.imagesToDelete.length > 0) {
        submitData.append(
          'deleteImageIds',
          formData.value.imagesToDelete.join(',')
        );
      }

      // Redosled postojećih slika
      const existingImageIds = formData.value.images
        .filter(
          (img) =>
            img.isExisting &&
            img.id &&
            !formData.value.imagesToDelete.includes(img.id)
        )
        .map((img) => img.id);

      if (existingImageIds.length > 0) {
        submitData.append('imageOrder', existingImageIds.join(','));
      }

      // Glavna slika (samo ako nije nova ili rotirana)
      if (
        !needsMainImageUpdate &&
        formData.value.images.length > 0 &&
        formData.value.images[0].id
      ) {
        const mainImageChanged =
          !originalProduct.value.pictures ||
          originalProduct.value.pictures.length === 0 ||
          originalProduct.value.pictures[0].id !== formData.value.images[0].id;

        if (mainImageChanged) {
          submitData.append('mainImageId', formData.value.images[0].id);
        }
      }

      // ----- DODAVANJE NOVIH SLIKA U FORMDATA -----
      if (newImages.length > 0) {
        console.log(`Procesiranje ${newImages.length} novih slika`);

        for (let i = 0; i < newImages.length; i++) {
          const image = newImages[i];

          console.log(`Procesiranje slike ${i + 1}/${newImages.length}`, {
            hasFile: !!image.file,
            isFileObject: image.file instanceof File,
            hasPreview: !!image.preview,
            order: image.order,
            isMain: image.isMain,
          });

          // Ako je file ispravan, koristi ga direktno
          if (image.file instanceof File) {
            // Primeni rotaciju na File objekat ako postoji rotacija
            if (image.rotation && image.rotation > 0) {
              try {
                console.log(
                  `Nova slika ${i + 1} ima rotaciju od ${
                    image.rotation
                  }°, primenjujem...`
                );
                const rotatedFile = await applyRotationToImage(
                  image.file,
                  image.rotation
                );
                console.log(
                  `Rotacija primenjena na novu sliku: ${rotatedFile.name}, size: ${rotatedFile.size}`
                );
                submitData.append('images', rotatedFile);
              } catch (err) {
                console.error(`Greška pri rotiranju nove slike: ${err}`);
                // Ako rotacija ne uspe, pošalji originalnu sliku
                submitData.append('images', image.file);
              }
            } else {
              // Ako nema rotacije, pošalji sliku direktno
              submitData.append('images', image.file);
            }
            continue;
          }

          // Ako file nije ispravan, a imamo preview, rekonstruiši File iz preview-a
          if (image.preview && image.preview.startsWith('data:')) {
            try {
              // Konvertuj base64 preview u Blob pa u File
              const response = await fetch(image.preview);
              const blob = await response.blob();

              // Kreiraj ime fajla sa ekstenzijom na osnovu MIME tipa
              let extension = 'jpg'; // Podrazumevana ekstenzija
              if (blob.type) {
                const mimeType = blob.type.split('/');
                if (mimeType.length > 1) {
                  extension = mimeType[1].split(';')[0]; // Uzmi ekstenziju iz MIME tipa
                }
              }

              const fileName = `image_${Date.now()}_${i}.${extension}`;
              const newFile = new File([blob], fileName, {
                type: blob.type || 'image/jpeg',
              });

              console.log(
                `Slika ${i + 1} rekonstruisana iz preview-a: ${
                  newFile.name
                }, size: ${newFile.size}`
              );

              // Dodaj rekonstruisani file u FormData
              if (image.rotation && image.rotation > 0) {
                try {
                  console.log(
                    `Rekonstruisana slika ${i + 1} ima rotaciju od ${
                      image.rotation
                    }°, primenjujem...`
                  );
                  const rotatedFile = await applyRotationToImage(
                    newFile,
                    image.rotation
                  );
                  console.log(
                    `Rotacija primenjena na rekonstruisanu sliku: ${rotatedFile.name}, size: ${rotatedFile.size}`
                  );
                  submitData.append('images', rotatedFile);
                } catch (err) {
                  console.error(
                    `Greška pri rotiranju rekonstruisane slike: ${err}`
                  );
                  // Ako rotacija ne uspe, pošalji nerotiranu sliku
                  submitData.append('images', newFile);
                }
              } else {
                // Ako nema rotacije, pošalji sliku direktno
                submitData.append('images', newFile);
              }
            } catch (err) {
              console.error(
                `Greška pri konverziji preview-a u file za sliku ${i + 1}:`,
                err
              );
            }
          } else {
            console.error(
              `Slika ${i + 1} nema ni validan file ni preview, preskačem je.`
            );
          }
        }
      }

      // ----- ROTIRANJE POSTOJEĆIH SLIKA -----
      if (existingImagesToRotate.length > 0) {
        console.log(
          `Procesiranje ${existingImagesToRotate.length} slika za rotaciju`
        );

        for (let i = 0; i < existingImagesToRotate.length; i++) {
          const image = existingImagesToRotate[i];

          if (!image.id || !image.path) {
            console.error(`Slika za rotaciju ${i + 1} nema ID ili path`, image);
            continue;
          }

          try {
            const sourceUrl = image.preview || getImageUrl(image.path);

            const response = await fetch(sourceUrl);
            const blob = await response.blob();

            const fileName =
              image.path.split('/').pop() || `rotated_${Date.now()}_${i}.jpg`;
            const imageFile = new File([blob], fileName, {
              type: blob.type || 'image/jpeg',
            });

            const rotatedFile = await applyRotationToImage(
              imageFile,
              image.rotation || 0
            );

            console.log(
              `Rotirana slika ${i + 1}/${existingImagesToRotate.length}: ${
                rotatedFile.name
              }, size: ${rotatedFile.size} bytes`
            );

            if (!formData.value.imagesToDelete.includes(image.id)) {
              formData.value.imagesToDelete.push(image.id);
            }

            submitData.set(
              'deleteImageIds',
              formData.value.imagesToDelete.join(',')
            );

            submitData.append('images', rotatedFile);
          } catch (err) {
            console.error(
              `Greška pri rotiranju postojeće slike ${i + 1}:`,
              err
            );
          }
        }
      }

      // Debug
      console.log('====== FINALNI FORM DATA ZA SLANJE ======');
      for (const pair of submitData.entries()) {
        if (pair[1] instanceof File) {
          console.log(
            `${pair[0]}: File - name: ${(pair[1] as File).name}, size: ${
              (pair[1] as File).size
            } bytes`
          );
        } else {
          console.log(`${pair[0]}: ${pair[1]}`);
        }
      }

      // Ako je potrebna posebna obrada glavne slike (nova ili rotirana), radimo to u dva koraka
      if (needsMainImageUpdate) {
        console.log(
          'Potreban je update glavne slike nakon dodavanja novih slika'
        );

        // 1. Prvi zahtev - dodaj/izmeni sve
        console.log(
          `Šaljem prvi PUT zahtev na: /api/products/${formData.value.id}`
        );
        const initialResponse = await putFormData(
          `/api/products/${formData.value.id}`,
          submitData
        );

        console.log('Prvi odgovor od API-ja:', initialResponse.data);

        if (initialResponse.data?.status === 'success') {
          // 2. Sada kada su slike sačuvane, možemo postaviti glavnu sliku
          const updatedProduct = initialResponse.data.data;

          // Pronađi novu sliku koja treba da bude glavna
          let mainImageId = '';

          if (isNewImageMain) {
            // Ako je nova slika trebala da bude glavna, to je prva nova slika u nizu
            const newPictures = updatedProduct.pictures.filter(
              (pic: PictureResponse) =>
                !originalProduct.value.pictures.some(
                  (origPic: PictureResponse) => origPic.id === pic.id
                )
            );

            if (newPictures.length > 0) {
              mainImageId = newPictures[0].id;
              console.log(`Nova slika kao glavna, ID: ${mainImageId}`);
            } else {
              console.log('Nisu pronađene nove slike u odgovoru');
            }
          } else if (isRotatedImageMain) {
            // Ako je rotirana slika bila glavna, pronađi sliku koja ima isti redosled
            const mainRotatedInfo = rotatedImagesInfo.find(
              (info) => info.isMain
            );

            if (mainRotatedInfo) {
              // Slike na serveru su sortirane po redosledu, pa možemo da koristimo isto indeks
              // za pronalaženje nove rotirane slike
              const mainRotatedOrder = mainRotatedInfo.order;

              // Pronađi sliku koja je na istoj poziciji
              const newPictures = updatedProduct.pictures.filter(
                (pic: PictureResponse) =>
                  !originalProduct.value.pictures.some(
                    (origPic: PictureResponse) => origPic.id === pic.id
                  )
              );

              if (newPictures.length > 0) {
                // Sortiraj nove slike po order polju
                const sortedNewPics = [...newPictures].sort(
                  (a, b) => a.order - b.order
                );

                // Postavi onu koja je na istoj poziciji kao glavna
                if (sortedNewPics.length >= mainRotatedOrder) {
                  mainImageId = sortedNewPics[mainRotatedOrder - 1].id;
                  console.log(`Rotirana slika kao glavna, ID: ${mainImageId}`);
                } else {
                  // Ako ne možemo da nađemo tačnu poziciju, uzmimo prvu novu sliku
                  mainImageId = sortedNewPics[0].id;
                  console.log(
                    `Uzimamo prvu novu sliku kao glavnu, ID: ${mainImageId}`
                  );
                }
              } else {
                console.log('Nisu pronađene nove slike u odgovoru');
              }
            }
          }

          if (mainImageId) {
            console.log(`Postavljanje ID ${mainImageId} kao glavne slike`);

            // Drugi zahtev samo za ažuriranje glavne slike
            const mainImageData = new FormData();
            // KLJUČNA ISPRAVKA: Dodajemo main image ID u drugi zahtev
            mainImageData.append('mainImageId', mainImageId);

            // KLJUČNA ISPRAVKA: Dodajemo opet osnovne podatke da garantujemo da će biti sačuvane
            mainImageData.append('name', formData.value.name);
            mainImageData.append('description', formData.value.description);
            mainImageData.append('price', formData.value.price);
            mainImageData.append('categoryId', formData.value.categoryId);

            console.log(
              `Šaljem drugi PUT zahtev za glavnu sliku na: /api/products/${formData.value.id}`
            );
            const finalResponse = await putFormData(
              `/api/products/${formData.value.id}`,
              mainImageData
            );

            console.log(
              'Odgovor nakon postavljanja glavne slike:',
              finalResponse.data
            );

            if (finalResponse.data?.status === 'success') {
              successMessage.value =
                'Proizvod je uspešno ažuriran sa novom glavnom slikom!';
              originalProduct.value = finalResponse.data.data;
            }
          } else {
            // Ako nismo mogli pronaći ID za glavnu sliku, koristimo rezultat prvog zahteva
            successMessage.value = 'Proizvod je uspešno ažuriran!';
            originalProduct.value = initialResponse.data.data;
          }

          showToast(
            successMessage.value || 'Proizvod je uspešno ažuriran!',
            'success'
          );

          // Redirekcija nakon oba zahteva
          setTimeout(() => {
            router.push(`/product/${originalProduct.value.slug}`);
          }, 1500);

          return; // Završi funkciju ovde jer smo obradili sve
        }
      }

      // Standardni put ako nema posebne obrade glavne slike
      console.log(
        `Šaljem standardni PUT zahtev na: /api/products/${formData.value.id}`
      );
      const response = await putFormData(
        `/api/products/${formData.value.id}`,
        submitData
      );

      console.log('Odgovor od API-ja:', response.data);

      if (response.data && response.data.status === 'success') {
        successMessage.value = 'Proizvod je uspešno ažuriran!';
        showToast('Proizvod je uspešno ažuriran!', 'success');

        // Ažuriramo originalProduct sa novim podacima
        originalProduct.value = response.data.data;

        // Redirekcija na stranicu proizvoda
        setTimeout(() => {
          router.push(`/product/${originalProduct.value.slug}`);
        }, 1500);
      }
    } catch (err: any) {
      console.error('Greška pri ažuriranju proizvoda:', err);

      if (err.response) {
        console.error('Server response:', {
          status: err.response.status,
          data: err.response.data,
          headers: err.response.headers,
        });
      }

      if (err.response && err.response.data && err.response.data.message) {
        error.value = err.response.data.message;
      } else {
        error.value =
          'Došlo je do greške pri ažuriranju proizvoda. Pokušajte ponovo.';
      }

      if (error.value) {
        showToast(error.value, 'error');
      } else {
        showToast('Došlo je do greške pri ažuriranju proizvoda', 'error');
      }
    } finally {
      isLoading.value = false;
    }
  };

  // Validacija forme
  const validateForm = async (): Promise<{
    isValid: boolean;
    errors: Record<string, string>;
  }> => {
    const errors: Record<string, string> = {};

    if (!formData.value.name.trim()) {
      errors.name = 'Naziv proizvoda je obavezan';
    } else if (formData.value.name.length < 3) {
      errors.name = 'Naziv proizvoda mora imati najmanje 3 karaktera';
    } else if (formData.value.name.length > 100) {
      errors.name = 'Naziv proizvoda ne sme biti duži od 100 karaktera';
    }

    if (!formData.value.description.trim()) {
      errors.description = 'Opis proizvoda je obavezan';
    } else if (formData.value.description.length < 10) {
      errors.description = 'Opis proizvoda mora imati najmanje 10 karaktera';
    }

    if (!formData.value.price.trim()) {
      errors.price = 'Cena je obavezna';
    } else if (
      isNaN(parseFloat(formData.value.price)) ||
      parseFloat(formData.value.price) <= 0
    ) {
      errors.price = 'Cena mora biti pozitivan broj';
    }

    if (!formData.value.categoryId) {
      errors.categoryId = 'Kategorija je obavezna';
    } else {
      // Dodatna validacija kategorije - mora biti podkategorija
      try {
        const isSubcategory = await categoryService.isSubcategory(
          formData.value.categoryId
        );
        if (!isSubcategory) {
          errors.categoryId = 'Morate izabrati podkategoriju';
        }
      } catch (err) {
        console.error('Greška pri proveri kategorije:', err);
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  // Watch for route changes to load product data
  watch(
    () => route.params.id,
    async (newId) => {
      if (newId) {
        await loadProduct(newId as string);
      }
    },
    { immediate: true }
  );

  // Funkcija za otkazivanje izmena
  const cancelChanges = () => {
    if (originalProduct.value) {
      console.log('Otkazivanje svih izmena...');

      // Resetuj formData na originalne vrednosti
      loadProduct(formData.value.id);

      // Obavesti korisnika
      showToast('Izmene su otkazane', 'info');
    }
  };

  return {
    formData,
    isLoading,
    error,
    successMessage,
    categories,
    toast,
    isDataLoaded,
    loadCategories,
    loadProduct,
    addImage,
    removeImage,
    setMainImage,
    rotateImage,
    updateProduct,
    validateForm,
    showToast,
    cancelChanges,
    originalProduct,
  };
}
